import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, ArrowUpRight, Trash2 } from "lucide-react";
import { api, dateTR, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const empty = { name: "", address: "", description: "", image_url: "", status: "aktif" };

export default function Buildings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/buildings").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Bina adı zorunlu");
    setBusy(true);
    try {
      await api.post("/buildings", form);
      toast.success("Bina eklendi");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bina ve tüm bağlı kayıtlar silinecek. Onaylıyor musunuz?")) return;
    try {
      await api.delete(`/buildings/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="buildings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Yalıtım · Binalar</p>
          <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl md:text-5xl">Proje (Bina) Yönetimi</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="building-add" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-5 py-3 font-display uppercase tracking-[0.18em] text-xs transition">
              <Plus className="h-4 w-4" /> Yeni Bina
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">Yeni Bina Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <Field label="Bina Adı *"><input data-testid="building-input-name" value={form.name} onChange={set("name")} className="kc-input" required /></Field>
              <Field label="Adres"><input data-testid="building-input-address" value={form.address} onChange={set("address")} className="kc-input" /></Field>
              <Field label="Açıklama"><textarea rows={2} value={form.description} onChange={set("description")} className="kc-input resize-none" /></Field>
              <Field label="Kapak Görseli (URL)"><input value={form.image_url} onChange={set("image_url")} className="kc-input" placeholder="https://..." /></Field>
              <Field label="Durum">
                <select value={form.status} onChange={set("status")} className="kc-input">
                  <option value="aktif" className="bg-[#0a0a0a]">Aktif</option>
                  <option value="tamamlandi" className="bg-[#0a0a0a]">Tamamlandı</option>
                  <option value="durduruldu" className="bg-[#0a0a0a]">Durduruldu</option>
                </select>
              </Field>
              <button data-testid="building-save" disabled={busy} className="w-full bg-[#d4af37] text-[#121212] hover:bg-white disabled:opacity-60 py-3 font-display uppercase tracking-[0.2em] text-xs transition">
                {busy ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </form>
            <style>{`.kc-input { width:100%; background: transparent; border:1px solid rgba(255,255,255,0.12); padding:10px 12px; color:white; outline: none; } .kc-input:focus { border-color:#d4af37; }`}</style>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="md:col-span-2 xl:col-span-3 border border-dashed border-white/10 p-12 text-center text-white/45 font-mono text-xs uppercase tracking-[0.3em]" data-testid="buildings-empty">
            Henüz bina eklenmedi
          </div>
        ) : (
          items.map((b) => (
            <article key={b.id} className="group border border-white/8 bg-[#121212] hover:border-[#d4af37]/40 transition" data-testid={`building-row-${b.id}`}>
              <div className="relative aspect-[16/9] overflow-hidden bg-[#0a0a0a]">
                {b.image_url ? (
                  <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Building2 className="h-12 w-12 text-[#d4af37]/40" /></div>
                )}
                <span className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-[0.3em] bg-[#d4af37]/15 border border-[#d4af37]/30 text-[#d4af37] px-2 py-1">{b.status}</span>
              </div>
              <div className="p-5">
                <div className="font-display text-2xl tracking-tight">{b.name}</div>
                {b.address && <div className="text-white/55 text-sm mt-1 truncate">{b.address}</div>}
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{dateTR(b.created_at)}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => remove(b.id)} data-testid={`building-delete-${b.id}`} className="p-2 text-white/40 hover:text-[#d4af37] transition" title="Sil">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Link to={`/admin/yalitim/${b.id}`} data-testid={`building-open-${b.id}`} className="inline-flex items-center gap-2 text-[#d4af37] font-display text-xs uppercase tracking-[0.25em] hover:text-white transition">
                      Aç <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">{label}</div>
    {children}
  </label>
);
